/**
 * Link detection for model output and AI drafts (shared by API and web).
 *
 * Catches scheme links (`https://…`), `www.` links and bare `label.tld[/path]` domains such as
 * `evil.gr/promo` or shorteners (`bit.ly/x9`, `t.co/…`), including simple obfuscations like
 * `evil[.]gr`. Email addresses are not links here (PII redaction handles them).
 */

/** TLDs worth flagging in short marketing text (common gTLDs, EU ccTLDs, shortener TLDs). */
const TLDS = `
com net org info biz io co me ai app dev xyz site online shop store link click top club
live page website win gr eu cy uk de fr it es nl be at ch pt ie pl ro bg al mk tr ru ua
us ca cn in br au ly gl gd gy cc tv ws to sh su tk ml ga cf gq
`
  .trim()
  .split(/\s+/);

const DOT = String.raw`(?:\.|\[\.\]|\(\.\)|\[dot\]|\(dot\))`;
const LABEL = String.raw`[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?`;
const BARE = String.raw`(?<![@\w.-])${LABEL}(?:${DOT}${LABEL})*${DOT}(?:${TLDS.join('|')})(?![a-z0-9-])(?:[/?#][^\s]*)?`;
const SCHEME = String.raw`\b(?:https?|ftp):\/\/[^\s]+`;
const WWW = String.raw`\bwww${DOT}[^\s]+`;

const linkRe = () => new RegExp(`${SCHEME}|${WWW}|${BARE}`, 'giu');

/** Trailing punctuation that ends a sentence rather than the link. */
function trimTrailing(s: string): string {
  return s.replace(/[.,;:!?)»"'\]]+$/u, '');
}

/** Every link-like substring in `text` (deduplicated, in order). */
export function findLinks(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(linkRe())) {
    const link = trimTrailing(m[0]);
    if (link && !out.includes(link)) out.push(link);
  }
  return out;
}

export function containsLink(text: string): boolean {
  return findLinks(text).length > 0;
}

/** Remove links from `text`; returns the cleaned text and what was removed (for the user). */
export function stripLinks(text: string): { text: string; removed: string[] } {
  const removed: string[] = [];
  const cleaned = text.replace(linkRe(), (m) => {
    const link = trimTrailing(m);
    if (!removed.includes(link)) removed.push(link);
    return m.slice(link.length); // keep the sentence punctuation
  });
  return {
    text: cleaned
      .replace(/[ \t]{2,}/g, ' ')
      .replace(/ +([.,;:!?])/g, '$1')
      .trim(),
    removed,
  };
}

/** Replace every link with `by` (keeps trailing sentence punctuation), e.g. `<url>` before AI. */
export function replaceLinks(text: string, by: string): string {
  return text.replace(linkRe(), (m) => by + m.slice(trimTrailing(m).length));
}
